import React from "react";
import { Alert, Box, Typography } from "@mui/material";
import { useAppSelector } from "../../../../app/hooks";

const OPAUploadSummary: React.FC = () => {
    const { rows, phase } = useAppSelector(state => state.opaUpload);

    if (rows.length === 0) {
        return null;
    }

    const totalCount = rows.length;
    const validCount = rows.filter(r => r.status === 'valid' || r.status === 'valid-warning' || r.status === 'uploading' || r.status === 'uploaded').length;
    const warningCount = rows.filter(r => r.status === 'valid-warning').length;
    const invalidCount = rows.filter(r => r.status === 'invalid').length;
    const uploadedCount = rows.filter(r => r.status === 'uploaded').length;
    const failedCount = rows.filter(r => r.status === 'failed').length;
    const pendingCount = rows.filter(r => r.status === 'pending').length;
    const validatingCount = rows.filter(r => r.status === 'validating').length;
    const uploadingCount = rows.filter(r => r.status === 'uploading').length;

    // Calculate sum of valid row amounts (for review phase)
    const validAmountSum = rows
        .filter(r => r.status === 'valid' || r.status === 'valid-warning' || r.status === 'uploading' || r.status === 'uploaded')
        .reduce((sum, row) => {
            const amount = parseFloat(row.data.amount);
            return sum + (isNaN(amount) ? 0 : amount);
        }, 0);

    // Calculate sum of uploaded row amounts (for complete phase)
    const uploadedAmountSum = rows
        .filter(r => r.status === 'uploaded')
        .reduce((sum, row) => {
            const amount = parseFloat(row.data.amount);
            return sum + (isNaN(amount) ? 0 : amount);
        }, 0);

    const formatCurrency = (amount: number) => {
        return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    };

    // Different display based on phase
    if (phase === 'parsing' || phase === 'idle') {
        return (
            <Alert severity="info">
                {totalCount} row(s) loaded from file. Click "Validate" to begin validation.
            </Alert>
        );
    }

    if (phase === 'validating') {
        const processed = validCount + invalidCount;
        return (
            <Alert severity="info">
                Validating... {processed} of {totalCount} rows processed.
                {validatingCount > 0 && ` (${validatingCount} in progress)`}
            </Alert>
        );
    }

    if (phase === 'review') {
        if (validCount === 0) {
            return (
                <Alert severity="error">
                    <Typography variant="body2">
                        <strong>Validation Complete:</strong> No valid rows to upload.
                    </Typography>
                    <Typography variant="body2">
                        {invalidCount} of {totalCount} row(s) failed validation. Please fix errors and try again.
                    </Typography>
                </Alert>
            );
        }

        if (invalidCount > 0 || warningCount > 0) {
            return (
                <Alert severity={invalidCount > 0 ? "error" : "info"}>
                    <Typography variant="body2">
                        <strong>Validation Complete:</strong> {validCount} of {totalCount} row(s) valid.
                    </Typography>
                    {invalidCount > 0 && (
                        <Typography variant="body2">
                            {invalidCount} row(s) failed validation. Please fix all errors before uploading.
                        </Typography>
                    )}
                    {warningCount > 0 && (
                        <Typography variant="body2">
                            {warningCount} row(s) have warnings but can still be uploaded.
                        </Typography>
                    )}
                    {invalidCount === 0 && (
                        <Box mt={1}>
                            <Typography variant="body2">
                                <strong>Total amount to upload:</strong> {formatCurrency(validAmountSum)}
                            </Typography>
                        </Box>
                    )}
                </Alert>
            );
        }

        return (
            <Alert severity="success">
                <Typography variant="body2">
                    <strong>Validation Complete:</strong> All {validCount} row(s) are valid and ready to upload.
                </Typography>
                <Box mt={1}>
                    <Typography variant="body2">
                        <strong>Total amount to upload:</strong> {formatCurrency(validAmountSum)}
                    </Typography>
                </Box>
            </Alert>
        );
    }

    if (phase === 'uploading') {
        return (
            <Alert severity="info">
                Uploading... {uploadedCount + failedCount} of {validCount} valid row(s) processed.
                {uploadingCount > 0 && ` (${uploadingCount} in progress)`}
            </Alert>
        );
    }

    if (phase === 'complete') {
        if (failedCount === 0) {
            return (
                <Alert severity="success">
                    <Typography variant="body2">
                        <strong>Upload Complete:</strong> All {uploadedCount} adjustment(s) uploaded successfully!
                    </Typography>
                    <Box mt={1}>
                        <Typography variant="body2">
                            <strong>Total amount uploaded:</strong> {formatCurrency(uploadedAmountSum)}
                        </Typography>
                    </Box>
                </Alert>
            );
        }

        return (
            <Alert severity="warning">
                <Typography variant="body2">
                    <strong>Upload Complete:</strong> {uploadedCount} succeeded, {failedCount} failed.
                </Typography>
                {invalidCount > 0 && (
                    <Typography variant="body2">
                        {invalidCount} row(s) were skipped due to validation errors.
                    </Typography>
                )}
                <Box mt={1}>
                    <Typography variant="body2">
                        <strong>Total amount uploaded:</strong> {formatCurrency(uploadedAmountSum)}
                    </Typography>
                </Box>
            </Alert>
        );
    }

    return null;
};

export { OPAUploadSummary };
